import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/apiClient";
import type { AiControlSystem, AiEditableSystemKey, AiEditingState } from "../../api/types";
import { AiInstructionPartView, AiPartHead } from "./AiInstructionPartView";
import { fmtAiWhen } from "./aiControlUi";

const NOTE_MAX = 200;
const PART_MAX = 4000;

interface Props {
  systemKey: AiEditableSystemKey;
  system: AiControlSystem;
  editing: AiEditingState;
  /** After a save, a discard or a publish: the page reloads. */
  onChanged: () => void;
  onTest: () => void;
}

/**
 * G-149 S2 (OD-230) — the instructions with their editable parts open for
 * writing. One shared draft: saved here, tested on past searches, then
 * published. Locked parts stay as they are shown.
 */
export function AiInstructionsEditor({ systemKey, system, editing, onChanged, onTest }: Props) {
  const saved = useMemo(() => editing.draft?.texts ?? editing.liveTexts, [editing]);
  const savedNote = editing.draft?.note ?? "";
  const [texts, setTexts] = useState<Record<string, string>>(saved);
  const [note, setNote] = useState(savedNote);
  const [busy, setBusy] = useState<"save" | "discard" | "publish" | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset to what is stored only when that changes — a reload that brings
  // back the same draft must not wipe what is being typed.
  const savedKey = JSON.stringify([saved, savedNote]);
  useEffect(() => {
    setTexts(saved);
    setNote(savedNote);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- savedKey stands for both
  }, [savedKey]);

  const keys = Object.keys(editing.liveTexts);
  const dirty = keys.some((k) => (texts[k] ?? "") !== (saved[k] ?? "")) || note !== savedNote;
  const changed = new Set(keys.filter((k) => (texts[k] ?? "").trim() !== (editing.liveTexts[k] ?? "").trim()));
  const draft = editing.draft;
  const tooLong = keys.some((k) => (texts[k] ?? "").length > PART_MAX);
  const empty = keys.some((k) => (texts[k] ?? "").trim().length === 0);
  const canPublish =
    draft !== null &&
    !dirty &&
    draft.changedKeys.length > 0 &&
    note.trim().length > 0 &&
    (draft.tested || !editing.canTest);

  async function act(kind: "save" | "discard" | "publish") {
    setBusy(kind);
    setError(null);
    try {
      if (kind === "save") await api.saveAiInstructionsDraft(systemKey, { texts, note: note.trim() || null });
      else if (kind === "discard") await api.discardAiInstructionsDraft(systemKey);
      else await api.publishAiInstructions(systemKey, note.trim());
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "That did not work.");
    } finally {
      setBusy(null);
    }
  }

  function discard() {
    if (draft) void act("discard");
    else {
      setTexts(saved);
      setNote(savedNote);
    }
  }

  return (
    <div className="ai-control-editor">
      <section className="ai-control-parts" aria-label="Instructions">
        {system.parts.map((part, index) => {
          if (part.locked || part.key === null) {
            return <AiInstructionPartView key={`${part.title ?? ""}-${index}`} part={part} startOpen />;
          }
          const key = part.key;
          const id = `ai-part-${key}`;
          const value = texts[key] ?? "";
          return (
            <div key={key} className="ai-control-part is-editable">
              <AiPartHead part={part} labelFor={id}>
                {changed.has(key) ? <span className="ai-control-pill is-warning">Changed</span> : null}
              </AiPartHead>
              <textarea
                id={id}
                className="ai-control-textarea"
                value={value}
                rows={Math.min(12, Math.max(3, Math.ceil(value.length / 90) + 1))}
                onChange={(e) => setTexts((prev) => ({ ...prev, [key]: e.target.value }))}
                disabled={busy !== null}
                aria-invalid={value.trim().length === 0 || value.length > PART_MAX}
              />
              {value.length > PART_MAX ? (
                <span className="ai-control-field-error">
                  {value.length} of {PART_MAX} characters
                </span>
              ) : null}
            </div>
          );
        })}
      </section>

      <div className="ai-control-draft-bar">
        <div className="field ai-control-note">
          <label htmlFor="ai-draft-note">What changed</label>
          <input
            id="ai-draft-note"
            type="text"
            value={note}
            maxLength={NOTE_MAX}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy !== null}
          />
        </div>
        <div className="ai-control-draft-state">
          {dirty ? (
            <span className="ai-control-pill is-warning">Not saved</span>
          ) : draft ? (
            <>
              <span className="ai-control-pill is-warning">Draft · not live</span>
              <span className={`ai-control-pill ${draft.tested ? "is-live" : "is-muted"}`}>
                {draft.tested ? "Tested" : "Not tested"}
              </span>
              <span className="muted">
                saved {fmtAiWhen(draft.savedAt)}
                {draft.savedBy ? ` · ${draft.savedBy}` : ""}
              </span>
            </>
          ) : null}
          {changed.size > 0 ? (
            <span className="muted">
              {changed.size} {changed.size === 1 ? "part" : "parts"} changed from live
            </span>
          ) : null}
        </div>
        {error ? <div className="alert error">{error}</div> : null}
        <div className="ai-control-actions">
          {draft || dirty ? (
            <button type="button" className="button plain" onClick={discard} disabled={busy !== null}>
              Discard draft
            </button>
          ) : null}
          <button
            type="button"
            className="button plain"
            onClick={() => void act("save")}
            disabled={busy !== null || !dirty || empty || tooLong}
          >
            {busy === "save" ? "Saving…" : "Save draft"}
          </button>
          {editing.canTest ? (
            <button
              type="button"
              className="button tinted"
              onClick={onTest}
              disabled={busy !== null || dirty || !draft || draft.changedKeys.length === 0}
            >
              Test on past searches
            </button>
          ) : null}
          <button
            type="button"
            className="button"
            onClick={() => void act("publish")}
            disabled={busy !== null || !canPublish}
          >
            {busy === "publish" ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}
